/**
 * JAISIU-3503 / processing-transparency — resolve a friendly, contextually
 * specific streaming-status phrase from a raw tool name + args.
 *
 * Replaces the generic "Running <tool>…" pill with operator-friendly
 * progress copy like "Reading PDF…", "Diffing merge…", "Calling tool X…".
 * The phrase is what the activity bubble and the composer live-step
 * show while the tool is mid-flight.
 *
 * Contract:
 *   - Pure: same input → same output, no host reads, no I/O.
 *   - Defensive: every input is treated as untrusted (tool name may be
 *     any string the gateway emits; args may be a JSON string, an object,
 *     or garbage). Falls back to a "Calling tool <name>…" copy when no
 *     richer phrase is available, so the operator never sees an empty
 *     bubble.
 *   - Ellipsis-bearing: every phrase ends in "…" so the chip reads as
 *     in-flight, not a noun. The activity renderer already ellipsizes
 *     generic labels, but doing it here keeps the phrase self-contained
 *     for any future caller (logs, telemetry, status toasts).
 *   - Length-bounded: target ≤ 80 chars so the pill never wraps; longer
 *     detail (file paths, URLs) is clipped with a trailing ellipsis.
 *
 * The map is intentionally small. Adding a phrase is a one-line change;
 * the unit tests pin the contract so accidental refactors fail loudly.
 */

const MAX_PHRASE_LENGTH = 80;

const TOOL_PHRASE_TABLE: ReadonlyArray<{
  readonly match: ReadonlyArray<string> | ((name: string) => boolean);
  readonly phrase: (name: string, args: unknown) => string;
}> = [
  // File reads — surface the file basename so the operator sees *what*
  // is being read, not just "read".
  {
    match: ["read", "read_file", "file_read", "read_pdf", "read_document", "pdf_read", "fs_read", "fs_read_file"],
    phrase: (_name: string, args: unknown): string => {
      const path = pickFirstString(args, ["path", "file_path", "filePath", "filename", "uri"]);
      const kind = guessFileKindFromPath(path);
      if (kind === "pdf") {
        return "Reading PDF…";
      }
      if (kind === "image") {
        return "Reading image…";
      }
      if (kind === "text") {
        return "Reading text…";
      }
      if (path) {
        return `Reading ${shortenPath(path)}…`;
      }
      return "Reading file…";
    },
  },
  // File writes / edits / patches — show the file basename.
  {
    match: ["write", "write_file", "create_file", "save_file"],
    phrase: (_name, args) => {
      const path = pickFirstString(args, ["path", "file_path", "filePath", "filename"]);
      return path ? `Writing ${shortenPath(path)}…` : "Writing file…";
    },
  },
  {
    match: ["edit", "edit_file", "str_replace", "str_replace_editor", "multi_edit"],
    phrase: (_name, args) => {
      const path = pickFirstString(args, ["path", "file_path", "filePath", "filename"]);
      return path ? `Editing ${shortenPath(path)}…` : "Editing file…";
    },
  },
  {
    match: ["apply_patch", "patch"],
    phrase: (_name, args) => {
      const path = pickFirstString(args, ["path", "file_path", "filePath", "filename"]);
      return path ? `Patching ${shortenPath(path)}…` : "Applying patch…";
    },
  },
  // Git / VCS operations
  {
    match: ["git_diff", "git_log", "git_status", "git_show"],
    phrase: (name) => {
      const verb =
        name === "git_diff"
          ? "Diffing"
          : name === "git_log"
            ? "Reading git log"
            : name === "git_status"
              ? "Checking git status"
              : "Inspecting commit";
      return `${verb}…`;
    },
  },
  {
    match: ["git_commit", "git_apply"],
    phrase: (name) => (name === "git_commit" ? "Committing… " : "Applying diff…"),
  },
  // Search
  {
    match: ["grep", "ripgrep", "search", "code_search", "rg"],
    phrase: (_name, args) => {
      const q = pickFirstString(args, ["query", "pattern", "q", "regex"]);
      return q ? `Searching for \u201c${truncate(q, 32)}\u2026\u201d…` : "Searching code…";
    },
  },
  {
    match: ["glob", "glob_file_search", "list_dir", "listdir", "ls", "tree"],
    phrase: (_name, args) => {
      const p = pickFirstString(args, ["path", "dir", "pattern", "glob"]);
      return p ? `Listing ${shortenPath(p)}…` : "Listing directory…";
    },
  },
  // Shell / bash
  {
    match: ["bash", "shell", "exec", "run_command", "run_shell", "terminal"],
    phrase: (_name, args) => {
      const cmd = pickFirstString(args, ["command", "cmd", "script"]);
      if (cmd) {
        const pretty = prettifyShellCommand(cmd);
        return `Running \`${truncate(pretty, 48)}\`…`;
      }
      return "Running command…";
    },
  },
  // Web
  {
    match: ["web_search", "search_web", "web_fetch", "fetch_url", "http_get", "curl"],
    phrase: (name, args) => {
      const url = pickFirstString(args, ["url", "uri", "endpoint", "query"]);
      if (url && name !== "web_search" && name !== "search_web") {
        return `Fetching ${truncate(shortenUrl(url), 56)}…`;
      }
      if (url) {
        return `Searching the web for \u201c${truncate(url, 32)}\u2026\u201d…`;
      }
      return "Searching the web…";
    },
  },
  // Subagents
  {
    match: ["sessions_spawn", "spawn_subagent", "delegate"],
    phrase: () => "Spawning subagent…",
  },
  // Memory / recall
  {
    match: ["memory_search", "memory_get", "memory_write", "palace_search"],
    phrase: (name) => {
      if (name === "memory_write") {
        return "Saving to memory…";
      }
      if (name === "memory_get") {
        return "Reading memory…";
      }
      return "Searching memory…";
    },
  },
  // Merging — the user explicitly called out "Diffing merge…"
  {
    match: ["merge", "git_merge", "rebase", "git_rebase"],
    phrase: (name) => (name === "rebase" || name === "git_rebase" ? "Rebasing…" : "Diffing merge…"),
  },
  // LLM / model call
  {
    match: ["llm_call", "model_call", "complete", "completion"],
    phrase: () => "Calling model…",
  },
];

const FALLBACK_PHRASE = "Calling tool…";

/**
 * Normalize a tool name from the gateway. The gateway emits tool names
 * in various shapes (`read`, `read_file`, `fs/read`, `fs.read`,
 * `readFile`, `read-file`); collapse to a comparable lower-cased
 * underscore form so the table above only needs to list each name
 * once.
 */
function normalizeToolName(raw: string): string {
  return raw.trim().toLowerCase().replace(/[.\-/]/g, "_").replace(/_+/g, "_");
}

/**
 * Pick the first string field from `args` whose key matches one of
 * `candidates`. Returns `null` if no candidate is a non-empty string.
 * Treats stringified JSON arg payloads the same as parsed objects so
 * the phrase is stable for the most common tool-arg shapes.
 */
function pickFirstString(args: unknown, candidates: ReadonlyArray<string>): string | null {
  if (!args) {
    return null;
  }
  let obj: Record<string, unknown> | null = null;
  if (typeof args === "string") {
    const trimmed = args.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          obj = parsed as Record<string, unknown>;
        }
      } catch {
        return null;
      }
    }
    // Plain string arg → treat as the first candidate so a bare
    // `bash "ls -la"` resolves the command for the phrase.
    if (!obj) {
      return trimmed;
    }
  } else if (typeof args === "object") {
    obj = args as Record<string, unknown>;
  }
  if (!obj) {
    return null;
  }
  for (const key of candidates) {
    const value = obj[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return null;
}

/** Trim a path to its basename, keeping the leading "./" or "/" if useful. */
function shortenPath(p: string): string {
  const trimmed = p.trim();
  if (!trimmed) {
    return "";
  }
  // Preserve the trailing leaf only — long absolute paths and node_modules
  // chains are unreadable in a pill.
  const idx = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const leaf = idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
  if (leaf.length <= 56) {
    return leaf;
  }
  return `${leaf.slice(0, 53)}…`;
}

/** Shorten a URL for the pill — keep the host + first 1-2 path segments. */
function shortenUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }
  // Strip scheme for readability.
  const noScheme = trimmed.replace(/^https?:\/\//i, "");
  if (noScheme.length <= 56) {
    return noScheme;
  }
  // Keep host + first path segment; truncate the rest.
  const slash = noScheme.indexOf("/");
  if (slash < 0) {
    return `${noScheme.slice(0, 53)}…`;
  }
  const host = noScheme.slice(0, slash);
  const rest = noScheme.slice(slash + 1);
  const next = rest.indexOf("/");
  const segment = next >= 0 ? rest.slice(0, next) : rest;
  if ((host + "/" + segment).length <= 56) {
    return `${host}/${segment}…`;
  }
  return `${host}/…`;
}

/** Best-effort file-kind guess from a path or filename. */
function guessFileKindFromPath(path: string | null): "pdf" | "image" | "text" | null {
  if (!path) {
    return null;
  }
  const lower = path.toLowerCase();
  if (lower.endsWith(".pdf")) {
    return "pdf";
  }
  if (/\.(png|jpe?g|gif|webp|svg|heic|heif|avif|bmp|tiff?)$/i.test(lower)) {
    return "image";
  }
  if (/\.(txt|md|markdown|rst|adoc|log|tsv|csv|json|ya?ml|toml|xml|html?|css|scss|less|js|jsx|ts|tsx|mjs|cjs|py|rb|rs|go|java|kt|swift|c|h|cc|cpp|hpp|cs|php|sh|bash|zsh|fish|sql|graphql|proto|env|ini|cfg|toml|lock)$/i.test(lower)) {
    return "text";
  }
  return null;
}

/** Pull a pretty one-liner out of a shell command for the pill. */
function prettifyShellCommand(cmd: string): string {
  return cmd.replace(/\s+/g, " ").trim();
}

/** Truncate a string with a single-character ellipsis, bounded. */
function truncate(s: string, max: number): string {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, Math.max(1, max - 1))}…`;
}

/** Bound a phrase to MAX_PHRASE_LENGTH so the pill never wraps. */
function boundPhrase(phrase: string): string {
  if (phrase.length <= MAX_PHRASE_LENGTH) {
    return phrase;
  }
  return `${phrase.slice(0, MAX_PHRASE_LENGTH - 1)}…`;
}

/**
 * Resolve a friendly streaming-status phrase for a tool invocation.
 * Pure function — no I/O, no host reads.
 *
 * @param toolName Raw tool name from the gateway. Normalized internally
 *                 so callers don't have to pre-shape the string.
 * @param args     Tool arguments (object, stringified JSON, or string).
 *                 Treated as untrusted; falls back to a generic phrase
 *                 when no readable field exists.
 * @returns        A contextually specific phrase ending in "…".
 */
export function resolveToolStatusPhrase(toolName: string, args?: unknown): string {
  const normalized = normalizeToolName(toolName);
  for (const { match, phrase } of TOOL_PHRASE_TABLE) {
    if (Array.isArray(match)) {
      if (!match.includes(normalized)) {
        continue;
      }
    } else if (typeof match === "function") {
      if (!match(normalized)) {
        continue;
      }
    } else {
      continue;
    }
    return boundPhrase(phrase(normalized, args));
  }
  // No specific phrase mapped — show the tool name verbatim so the
  // operator still sees *which* tool is running, not just a generic
  // "Calling tool…". A bare "tool" string (the gateway's fallback
  // name) collapses to the generic phrase.
  if (normalized && normalized !== "tool") {
    return boundPhrase(`Calling tool ${normalized}…`);
  }
  return FALLBACK_PHRASE;
}

/**
 * Resolve a streaming-status phrase for a lifecycle `phase` event
 * that doesn't carry a tool name. The lifecycle phases emitted by
 * the gateway are `thinking`, `tool`, `end`, `error`; we only
 * surface the in-flight ones.
 */
export function resolvePhaseStatusPhrase(phase: string): string | null {
  const normalized = phase.trim().toLowerCase();
  if (normalized === "thinking") {
    return "Thinking…";
  }
  if (normalized === "tool") {
    // Tool phase without a name shouldn't normally happen, but if
    // it does fall back to a generic "Running tool…" so the pill
    // doesn't go blank.
    return "Running tool…";
  }
  return null;
}
